// Lib
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

// Roma Components
import { Button, Field, Input } from '@milanote/roma';

// Components
import FinalForm from '../../../components/form/finalForm/FinalForm';
import FinalField from '../../../components/form/finalForm/FinalField';
import AuthErrorMessage from '../../../components/form/messages/AuthErrorMessage';
import { useTranslator } from '../../../components/i18n/useTranslator';
import { getValidationErrorMessage } from '../../../components/form/validation/getValidationErrorMessage';

// Hooks
import useGetSocialOauthError from '../../useGetSocialOauthError';
import useSaveVirtualKeyboardHeightToLocalStorage from '../../../utils/keyboard/useSaveVirtualKeyboardHeightToLocalStorage';

// Utils
import formSubmissionExceptionHandler from '../../../components/form/formSubmissionExceptionHandler';
import { shouldDisableSubmitButton } from '../../../components/form/finalForm/finalFormUtils';

// Constants
import { FIELDS } from '../../../../common/validation/validationConstants';

const LOGIN_FORM_NAME = 'login';
const LOGIN_FIELDS = [FIELDS.email, FIELDS.loginPassword];

const LoginForm = (props) => {
    const { deviceId, location, dispatchLogin } = props;
    const translate = useTranslator('auth', 'login.form');
    const translateValidationError = useTranslator('errors');

    const initialValues = useMemo(() => ({ deviceId }), [deviceId]);
    const oauthError = useGetSocialOauthError(location);

    useSaveVirtualKeyboardHeightToLocalStorage();

    const onSubmit = useCallback(
        async (values) => {
            try {
                await dispatchLogin(values);
            } catch (err) {
                return formSubmissionExceptionHandler({ form: LOGIN_FORM_NAME, sensitive: true })(err);
            }
        },
        [dispatchLogin],
    );

    return (
        <FinalForm
            className="auth-form"
            initialValues={initialValues}
            onSubmit={onSubmit}
            fields={LOGIN_FIELDS}
            name={LOGIN_FORM_NAME}
        >
            {(formState) => (
                <div className="flex flex-col gap-3xl">
                    {/* Prevent passwords from auto-filling after an social sign in error */}
                    {oauthError && (
                        <input
                            id="pwd"
                            className="fake-password"
                            tabIndex="-1"
                            aria-hidden="true"
                            type="password"
                            name="fakepasswordremembered"
                        />
                    )}

                    {/* Form fields */}
                    <div className="flex flex-col gap-2xl">
                        <FinalField field={FIELDS.email}>
                            {({ input, display }) => (
                                <Input
                                    {...input}
                                    size="md"
                                    type={FIELDS.email.type}
                                    maxLength={FIELDS.email.maxLength}
                                    label={translate('emailLabel')}
                                    isInvalid={!!display.error}
                                    hint={getValidationErrorMessage(display.error, translateValidationError)}
                                />
                            )}
                        </FinalField>

                        {/* Password with forgot password link */}
                        <FinalField field={FIELDS.loginPassword}>
                            {({ input, display }) => (
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                                    <Field.Label className="col-start-1 row-start-1" htmlFor={input.name} size="md">
                                        {translate('passwordLabel')}
                                    </Field.Label>
                                    <Input.Base
                                        {...input}
                                        size="md"
                                        type="password"
                                        maxLength={FIELDS.loginPassword.maxLength}
                                        isInvalid={!!display.error}
                                        wrapperClassName="col-span-2 row-start-2"
                                    />
                                    <Button
                                        href="/forgotten-password"
                                        routerOptions={{ keepParams: true }}
                                        color="link-gray"
                                        size="sm"
                                        className="col-start-2 row-start-1 justify-self-end font-normal underline hover:decoration-inherit *:data-text:underline-offset-1"
                                    >
                                        {translate('forgotPassword')}
                                    </Button>
                                    {display.error && (
                                        <span className="col-span-2 row-start-3 text-sm text-error-primary">
                                            {getValidationErrorMessage(display.error, translateValidationError)}
                                        </span>
                                    )}
                                </div>
                            )}
                        </FinalField>
                    </div>

                    <Button
                        type="submit"
                        size="md"
                        color="primary"
                        className="w-full"
                        isDisabled={shouldDisableSubmitButton(formState)}
                        isLoading={formState.submitting}
                    >
                        {translate('submitButton')}
                    </Button>

                    <AuthErrorMessage
                        primaryError={formState.submitError || oauthError}
                        hide={formState.modifiedSinceLastSubmit || formState.submitting}
                    />
                </div>
            )}
        </FinalForm>
    );
};

LoginForm.propTypes = {
    location: PropTypes.object,
    deviceId: PropTypes.string,
    dispatchLogin: PropTypes.func,
};

export default LoginForm;
